import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import { getSubStatus } from "../lib/hub";

const POLL_MAX = 8;

export default function BillingReturn({ mode = "success" }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(mode === "cancel" ? "cancelled" : "checking");
  const [planId, setPlanId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (mode === "cancel" || !sessionId) return;
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const d = await getSubStatus(sessionId);
        setPlanId(d.plan_id);
        if (d.payment_status === "paid") { setState("paid"); return; }
        if (d.payment_status === "failed" || d.payment_status === "expired") { setState("failed"); return; }
      } catch { /* keep polling */ }
      tries += 1;
      if (tries >= POLL_MAX) { setState("pending"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, mode]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4 py-16" data-testid="billing-return-page">
      <div className="max-w-md w-full text-center border border-black/10 bg-white p-8">
        {state === "checking" && (
          <div data-testid="billing-checking">
            <Loader2 className="w-12 h-12 mx-auto text-taven-gold animate-spin" />
            <h1 className="mt-5 text-2xl font-black font-display">Confirming your payment…</h1>
            <p className="mt-2 text-sm text-muted-foreground">This takes just a moment.</p>
          </div>
        )}
        {state === "paid" && (
          <div data-testid="billing-paid">
            <CheckCircle2 className="w-12 h-12 mx-auto text-green-600" />
            <h1 className="mt-5 text-2xl font-black font-display">Membership Activated</h1>
            <p className="mt-2 text-sm text-muted-foreground">Your TAVEN membership is now active. Build. Earn. Grow. Leave a Legacy.</p>
            <button onClick={() => navigate("/my-membership")} className="mt-6 inline-flex items-center gap-1 bg-taven-black text-white px-6 py-3 text-xs font-bold uppercase tracking-wider hover:bg-taven-orange" data-testid="billing-go-membership">
              View My Membership <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        {state === "pending" && (
          <div data-testid="billing-pending">
            <Loader2 className="w-12 h-12 mx-auto text-taven-gold" />
            <h1 className="mt-5 text-2xl font-black font-display">Payment Processing</h1>
            <p className="mt-2 text-sm text-muted-foreground">Your payment is being finalized. Your membership will activate automatically once confirmed.</p>
            <Link to="/my-membership" className="mt-6 inline-flex items-center gap-1 border border-black/15 px-5 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-taven-gray" data-testid="billing-check-later">Check My Membership</Link>
          </div>
        )}
        {state === "failed" && (
          <div data-testid="billing-failed">
            <XCircle className="w-12 h-12 mx-auto text-red-600" />
            <h1 className="mt-5 text-2xl font-black font-display">Payment Not Completed</h1>
            <p className="mt-2 text-sm text-muted-foreground">We couldn't confirm your payment. No membership was activated. You can try again.</p>
            <Link to="/membership" className="mt-6 inline-flex items-center gap-1 bg-taven-black text-white px-6 py-3 text-xs font-bold uppercase tracking-wider hover:bg-taven-orange" data-testid="billing-retry">Back to Memberships</Link>
          </div>
        )}
        {state === "cancelled" && (
          <div data-testid="billing-cancelled">
            <XCircle className="w-12 h-12 mx-auto text-black/40" />
            <h1 className="mt-5 text-2xl font-black font-display">Checkout Cancelled</h1>
            <p className="mt-2 text-sm text-muted-foreground">No charge was made. You can return anytime to complete your membership.</p>
            <Link to="/membership" className="mt-6 inline-flex items-center gap-1 bg-taven-black text-white px-6 py-3 text-xs font-bold uppercase tracking-wider hover:bg-taven-orange" data-testid="billing-back">Back to Memberships</Link>
          </div>
        )}
      </div>
    </div>
  );
}
